import { useContext } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';

import type { ContextValue } from '../../components/SunriseHoldoutExperimentContextProvider';
import { SunriseHoldoutExperimentContext } from '../../components/SunriseHoldoutExperimentContextProvider';

import type { AmountInputProps, AmountInputState } from './amountInputState';
import { amountInputState, isAmountInputState } from './amountInputState';
import { type EnabledProps, enabledState, type EnabledState } from './enabledState';
import type { HighlightTextProps, HighlightTextState } from './highlightTextState';
import { highlightTextState, isHighlightTextState } from './highlightTextState';
import {
	isMembershipFormState,
	type MembershipFormProps,
	type MembershipFormState,
	membershipFormState,
} from './membershipFormState';
import type { SelectionProps, SelectionState } from './selectionState';
import { isSelectionState, selectionState } from './selectionState';
import {
	isValidatePayPalState,
	type ValidatePayPalProps,
	type ValidatePayPalState,
	validatePayPalState,
} from './validatePaypalState';

export const ENABLED_VARIANTS = ['control', 'variant_1', 'variant_2', 'variant_3', 'variant_4'];

export type Props =
	| EnabledProps
	| HighlightTextProps
	| MembershipFormProps
	| SelectionProps
	| ValidatePayPalProps
	| AmountInputProps
	| undefined;

type State =
	| EnabledState
	| HighlightTextState
	| MembershipFormState
	| SelectionState
	| ValidatePayPalState
	| AmountInputState;

export function useSunriseHoldoutExperiment(props?: EnabledProps): EnabledState;
export function useSunriseHoldoutExperiment(props: HighlightTextProps): HighlightTextState;
export function useSunriseHoldoutExperiment(props: MembershipFormProps): MembershipFormState;
export function useSunriseHoldoutExperiment(props: SelectionProps): SelectionState;
export function useSunriseHoldoutExperiment(props: ValidatePayPalProps): ValidatePayPalState;
export function useSunriseHoldoutExperiment(props: AmountInputProps): AmountInputState;
export function useSunriseHoldoutExperiment(props?: Props): State {
	const {
		setUserInteracted,
		userInteracted,
		variant,
		noSelectionMadeError,
		setNoSelectionMadeError,
		shouldUseBlueCards,
	} = useContext(SunriseHoldoutExperimentContext);

	const track = useTracking();

	const enabled = isEnabledVariant(variant);
	const isSunriseEnabled = enabled || isSunriseEnabledInProps(props);

	if (isHighlightTextState(props)) return highlightTextState(props.selected, variant, enabled);
	if (isAmountInputState(props)) return amountInputState({ ...props, enabled, shouldUseBlueCards });
	if (isValidatePayPalState(props))
		return validatePayPalState({ ...props, variant, userInteracted, setNoSelectionMadeError });

	if (isSelectionState(props)) return selectionState(isSunriseEnabled, props.checked, variant);
	if (isMembershipFormState(props)) {
		return membershipFormState({
			...props,
			userInteracted,
			setUserInteracted,
			isSunriseEnabled,
			enabled,
			noSelectionMadeError,
			setNoSelectionMadeError,
			variant,
			track,
		});
	}

	return enabledState({
		enabled,
		isSunriseEnabled,
		variant,
		shouldUseBlueCards,
	});
}

function isEnabledVariant(variant: ContextValue['variant']) {
	return !!variant && ENABLED_VARIANTS.includes(variant);
}

function isSunriseEnabledInProps(props: Props) {
	return !!(props && 'isSunriseEnabled' in props && props.isSunriseEnabled);
}
